import axios from "axios";
import authAxios from "./authAxios";
import { setCookie, getCookie, removeCookie } from "./cookie";
import { returnToAdministrator } from "./impersonation";
import resetPersistedState from "./resetPersistedState";

import { reportFatalRequestError } from "components/layouts/errors/GlobalRequestErrorOverlay";
import { reportAccessDenied } from "components/layouts/errors/GlobalAccessDeniedOverlay";

// Regular API requests - shorter timeout
const apiAxios = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api/v1`,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json"
  }
});

// File upload requests - longer timeout
export const apiAxiosUpload = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api/v1`,
  timeout: 300000,
  headers: {
    "Content-Type": "multipart/form-data"
  }
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

const redirectToLogin = () => {
  window.location.href = `${process.env.REACT_APP_CLIENT_URL}/authentication/login`;
};

// The session could not be refreshed. While an administrator views as someone, that
// is the view-as session ending (two hours, or refused) — go back to the
// administrator, whose own session is intact, instead of the sign-in page.
const leaveAfterAuthFailure = async () => {
  if (returnToAdministrator()) {
    await resetPersistedState(); // the store holds the viewed person's records
    window.location.href = "/";
    return;
  }
  redirectToLogin();
};

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh the access token using the stored refresh token.
// authAxios.baseURL is already `${API_URL}/auth`, so the path here is just "/refresh_token".
const refreshToken = async () => {
  const refresh_token =
    getCookie("refresh_token") || localStorage.getItem("refresh_token");

  if (!refresh_token) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await authAxios.post("/refresh_token", { refresh_token });
    const {
      access_token,
      refresh_token: new_refresh_token,
      token_expiration
    } = response.data;

    if (!access_token) {
      throw new Error("Invalid refresh response");
    }

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", new_refresh_token);
    localStorage.setItem("token_expiration", token_expiration);

    setCookie("access_token", access_token, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });
    setCookie("refresh_token", new_refresh_token, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });
    setCookie("token_expiration", token_expiration, {
      httpOnly: false,
      secure: false,
      sameSite: "lax"
    });

    // Permissions may have changed since login — refresh the cached profile
    // (fire-and-forget) so role/permission updates take effect without re-login.
    // Dynamic import avoids a circular dependency (store → slices → apiAxios).
    import("redux/store")
      .then(({ store }) =>
        import("redux/profileUserSlice").then(({ getProfileUser }) =>
          store.dispatch(getProfileUser())
        )
      )
      .catch(() => {});

    return access_token;
  } catch (error) {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_expiration");

    removeCookie("access_token");
    removeCookie("refresh_token");
    removeCookie("token_expiration");
    throw error;
  }
};

// Returns true when the stored token is expired or within 5 minutes of expiry.
const isTokenExpired = () => {
  const expirationTime =
    getCookie("token_expiration") || localStorage.getItem("token_expiration");
  if (!expirationTime) return true;

  const now = Math.floor(Date.now() / 1000);
  const bufferTime = 300; // 5-minute buffer
  return now >= parseInt(expirationTime) - bufferTime;
};

// Reads the `exp` claim straight from the JWT payload (seconds since epoch), so we don't
// depend on the separately-stored token_expiration which can drift out of sync.
const jwtExp = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)).exp || null;
  } catch (_) {
    return null;
  }
};

// Returns a guaranteed-fresh access token (refreshing first if expired/near-expiry).
// Use when a token must be handed to something outside axios — e.g. an iframe src
// to Hub-Sign — where the request interceptor's auto-refresh doesn't run.
export const getFreshAccessToken = async () => {
  let token = getCookie("access_token") || localStorage.getItem("access_token") || "";
  const exp = jwtExp(token);
  const now = Math.floor(Date.now() / 1000);
  const jwtDead = exp ? now >= exp - 60 : false; // trust the token's own exp

  if (!token || jwtDead || isTokenExpired()) {
    try {
      token = await refreshToken();
    } catch (_) {
      /* fall through to whatever we have */
    }
    token = token || getCookie("access_token") || localStorage.getItem("access_token") || "";
  }
  return token;
};

// Persist diagnostic context to sessionStorage so error pages can read it.
// Called for all 4xx/5xx responses before the promise is rejected.
export const saveErrorContext = (axiosError) => {
  try {
    const config = axiosError.config || {};
    const response = axiosError.response || {};
    const context = {
      status:    response.status,
      method:    (config.method || 'GET').toUpperCase(),
      url:       config.url || '',
      baseURL:   config.baseURL || '',
      page:      window.location.pathname,
      message:   response.data?.error || response.data?.message || axiosError.message || '',
      detail:    response.data ?? null,
      timestamp: new Date().toISOString(),
    };
    sessionStorage.setItem('_portal_last_error', JSON.stringify(context));
  } catch (_) {
    // Never throw from a diagnostic helper
  }
};

// Attach request/response interceptors to an axios instance.
const setupInterceptors = instance => {
  // ── Request interceptor ─────────────────────────────────────────────────────
  // Proactively refresh the token before the request if it's about to expire.
  instance.interceptors.request.use(
    async config => {
      const token =
        getCookie("access_token") || localStorage.getItem("access_token");

      if (!token) {
        return config;
      }

      if (!isTokenExpired()) {
        config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      }

      // Token expired — refresh before sending
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          isRefreshing = false;
          processQueue(null, newToken);
          config.headers["Authorization"] = `Bearer ${newToken}`;
          return config;
        } catch (error) {
          isRefreshing = false;
          processQueue(error, null);
          leaveAfterAuthFailure();
          return Promise.reject(error);
        }
      }

      // Another request is already refreshing — queue this one
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: newToken => {
            config.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(config);
          },
          reject
        });
      });
    },
    error => Promise.reject(error)
  );

  // ── Response interceptor ────────────────────────────────────────────────────
  // React to 401 responses by trying a token refresh once, then retrying.
  instance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      const status = error?.response?.status;

      // A cancelled request also arrives with no response, and it is not a
      // failure of any kind — it means the caller moved on (a superseded search,
      // a component unmounting). Treating it as an unreachable server threw the
      // blocking "Network error" screen over a page that was working fine.
      // Checked before everything else so no later branch can misread it.
      if (axios.isCancel?.(error) || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
        return Promise.reject(error);
      }

      if (!error.response) {
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          const isUpload = originalRequest?.headers?.["Content-Type"]?.includes(
            "multipart/form-data"
          );
          const message = isUpload
            ? "File upload timeout. The file might be too large or your connection is slow. Please try again with a smaller file."
            : "Request timeout. Please try again.";
          // A single request timing out is NOT fatal — let the caller (redux
          // section error / inline card / toast) handle it so one slow widget
          // doesn't block the whole page. The blocking prompt is reserved for a
          // genuinely unreachable server (the network branch below).
          return Promise.reject(new Error(message));
        }

        // No response and not a timeout = server unreachable / offline. This is
        // app-wide fatal, so show the blocking refresh prompt.
        reportFatalRequestError("Network error. Please check your connection and refresh the page.");
        return Promise.reject(
          new Error("Network error. Please check your connection.")
        );
      }

      // 401 — reactive token refresh (only retry once per request)
      if (status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: newToken => {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                resolve(instance(originalRequest));
              },
              reject
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          isRefreshing = false;
          processQueue(null, newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          leaveAfterAuthFailure();
          return Promise.reject(refreshError);
        }
      }

      // Persist diagnostic context for all 4xx/5xx so error pages can display it
      if (status >= 400) {
        saveErrorContext(error);
      }

      if (status) {
        switch (status) {
          case 403: {
            // 403 khi ĐỌC dữ liệu = người dùng không được xem tài nguyên này →
            // dựng trang 403 thay vì để họ nhìn trang trống/dữ liệu cũ kèm một
            // toast dễ bỏ sót. 403 khi ghi (POST/PUT/PATCH/DELETE) vẫn giữ toast
            // để người dùng ở lại đúng chỗ đang thao tác.
            // Request nền (poll, widget phụ) tự loại trừ bằng
            // `skipAccessDeniedPage: true` — hoặc `skipErrorToast: true`, vốn đã
            // có nghĩa "request này tự xử lý lỗi, đừng báo ra toàn cục".
            const method = (originalRequest?.method || "get").toLowerCase();
            const isRead = method === "get";
            const quiet =
              originalRequest?.skipAccessDeniedPage ||
              originalRequest?.skipErrorToast;
            // Chỉ nhận message dạng chuỗi từ backend; các dạng khác (mảng lỗi
            // validate, object) để trang 403 dùng câu mặc định.
            const serverMessage = [
              error?.response?.data?.error,
              error?.response?.data?.message
            ].find(value => typeof value === "string" && value.trim());

            const pageShown =
              isRead &&
              !quiet &&
              reportAccessDenied({
                message: serverMessage,
                url: originalRequest?.url
              });

            const accessDeniedErr = new Error(
              "Access denied. You do not have permission to perform this action."
            );
            accessDeniedErr.status = 403;
            accessDeniedErr.isAccessDenied = true;
            accessDeniedErr.accessDeniedPageShown = Boolean(pageShown);
            return Promise.reject(accessDeniedErr);
          }
          case 404:
            if (originalRequest?.skipErrorToast) return Promise.reject(error);
            return Promise.reject(new Error("Resource not found."));
          case 413:
            return Promise.reject(
              new Error(
                "File is too large. Please reduce the file size and try again."
              )
            );
          case 422:
            // Preserve full error structure so Redux thunks can access error.response.data
            return Promise.reject(error);
          case 500:
            // A 500 on one request is handled by that caller (section error /
            // toast), not by blocking the whole page.
            return Promise.reject(
              new Error("Server error. Please try again later.")
            );
          default: {
            const message =
              error?.response?.data?.message ||
              `Server error (${status}). Please try again.`;
            return Promise.reject(new Error(message));
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

setupInterceptors(apiAxios);
setupInterceptors(apiAxiosUpload);

export default apiAxios;
